import {Component, OnDestroy, OnInit} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {constants} from '../../../_helpers/constants';
import {Subscription} from 'rxjs';
import {QualitaService} from '../../../_services/qualita.service';
import {AuthenticationService} from '../../../_services/authentication.service';
import {OrdineGroup} from '../../../_models/OrdineGroup';
import {Contenitore} from '../../../_models/Contenitore';
import {Ordine} from '../../../_models/Ordine';
import {AvanzamentoService} from '../../../_services/avanzamento.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {SaveMessage} from '../../../_helpers/save-message';
import {Movimento} from '../../../_models/Movimento';
import {LocalService} from '../../../_services/local.service';
import {Materiale} from '../../../_models/Materiale';
import {UnitConverterService} from '../../../_services/unit-converter.service';
import {LicenzeService} from '../../../_services/licenze.service';
import {MovimentiService} from '../../../_services/movimenti.service';
import {CqSequenceOutcome} from '../../../_models/CqSequenceOutcome';

@Component({
    selector: 'app-controlli-qualita-ordine',
    templateUrl: './controlli-qualita-ordine.component.html',
    styleUrls: ['./controlli-qualita-ordine.component.scss'],
    standalone: false
})
export class ControlliQualitaOrdineComponent  implements OnInit, OnDestroy {
  ordine: OrdineGroup;
  tipoControllo = 0;
  statoControlliQualita = 0;
  codBox = '';
  activeTransfer = false;
  codMagTransfer = '';
  codMagAreaTransfer = '';
  codMagTransferNc = '';
  codMagAreaTransferNc = '';
  qtaConferma = 0;
  qtaNonConforme = 0;
  checkMaterialeDistinta = true;
  subscriptions: Subscription;
  boxes: Contenitore[] = [];
  boxesOk: Contenitore[] = [];
  boxesNc: Contenitore[] = [];
  loading = false;

  // Modalità rientro terzista (feature 023, rev. 2026-05-19 post-refactor UX)
  // Riusa la card "Trasferimento Prodotto" esistente: i selettori magazzino sono le property
  // legacy codMagTransfer/codMagAreaTransfer/codMagTransferNc/codMagAreaTransferNc già dichiarate.
  // Le 4 property dedicate (magazzinoConforme, magazzinoNc, flagMagazzinoConformeActive,
  // flagMagazzinoNcActive) sono state droppate (clarification Session 2026-05-19 Q2).
  modaRientroTerzista = false;
  activeOrdineIndex = 0;
  movimentiRettificaCount: { conforme: number; nc: number } = { conforme: 0, nc: 0 };
  ordiniSaved: Set<number> = new Set<number>();
  checkListVisible = true;
  private autoAdvanceTimer: any = null;

  protected readonly constants = constants;
  constructor(
    public authService: AuthenticationService,
    public licenseService: LicenzeService,
    private qualitaService: QualitaService,
    private avanzamentoService: AvanzamentoService,
    private localService: LocalService,
    private movimentiService: MovimentiService,
    private saveMessage: SaveMessage,
    private organizzazioneService: OrganizzazioniService,
    private unitConverter: UnitConverterService,
    private modalCtrl: ModalController) {
    this.subscriptions = this.localService.searchOrdineGroup
      .subscribe((ordineGroup: OrdineGroup) => this.ordine = ordineGroup);
  }

  get ordineCount(): number {
    const ordini = this.ordine?.ordini;
    return ordini && ordini.length > 1 ? ordini.length : 1;
  }

  get activeOrdine(): Ordine {
    const ordini = this.ordine?.ordini;
    if (ordini && ordini.length > 0) {
      return ordini[this.activeOrdineIndex] ?? this.ordine;
    }
    return this.ordine;
  }

  get activeOrdineGroupForCheck(): OrdineGroup {
    // Wrapper OrdineGroup costruito dall'ordine attivo, passato a CheckControlliListComponent (FR-022 invariante)
    return { ...(this.activeOrdine as OrdineGroup) };
  }

  ngOnInit() {
    this.activeOrdineIndex = 0;
    this.hydrateActiveOrdine();
    this.applyDefaultsFromRisorsa();
    if (this.activeOrdine && this.activeOrdine.risorsa) {
      this.activeTransfer = this.activeOrdine.risorsa.avanzaMode === 2;
    }
    if (this.activeTransfer) {
      this.loading = true;
      this.localService.getMateriale()
        .finally(() => this.loading = false);
    }
    // Subscribe DOPO che componentProps sono applicati e lo stato locale è inizializzato.
    // Skip della prima emissione (replay del BehaviorSubject) per evitare di reagire al
    // valore stale di una sessione precedente di statoControlli.
    let firstEmission = true;
    this.subscriptions.add(this.qualitaService.statoControlli
      .subscribe((_stato: number) => {
        if (firstEmission) {
          firstEmission = false;
          return;
        }
        this.onStatoControlli(_stato);
      }));
  }

  ngOnDestroy() {
    if (this.autoAdvanceTimer) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
    this.subscriptions.unsubscribe();
  }

  private hydrateActiveOrdine = () => {
    // Pre-fill di `this.boxes` da `activeOrdine.contenitori` SOLO per:
    //  - modalità rientro terzista (FR-025: contenitori passati dalla pagina chiamante),
    //  - sequence mode (ordineCount > 1: navigazione fra ordini ricarica i contenitori).
    // Nel flusso legacy single-ordine (es. interfaccia-manuale) `this.boxes` resta vuoto:
    // l'utente scansiona i box via `app-box-input` → `selectBox` → `setMateriale` genera
    // i movimenti di scarico (tipoMovimento -1) su `materiale[...].movimenti`.
    if (this.modaRientroTerzista || this.ordineCount > 1) {
      const contenitori = this.activeOrdine?.contenitori ?? [];
      this.boxes = [...contenitori];
    } else {
      this.boxes = [];
    }
    this.qtaConferma = 0;
    this.qtaNonConforme = 0;
    this.updateQta();
  };

  private applyDefaultsFromRisorsa = () => {
    // Rev. 2026-05-19 (clarification Q2): prefill diretto sui selettori legacy
    // codMagTransfer/codMagTransferNc (riusati anche in modaRientroTerzista).
    const risorsa = this.activeOrdine?.risorsa;
    this.codMagTransfer = risorsa?.codMagazzino ?? '';
    this.codMagAreaTransfer = risorsa?.codMagArea ?? '';
    this.codMagTransferNc = risorsa?.codMagNc ?? '';
    this.codMagAreaTransferNc = risorsa?.codMagAreaNc ?? '';
  };

  private advanceToNextOrdine = () => {
    if (this.autoAdvanceTimer) {
      clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }
    const nextIndex = this.activeOrdineIndex + 1;
    if (nextIndex >= this.ordineCount) {
      this.statoControlliQualita = 2;
      const outcome: CqSequenceOutcome = {
        outcome: 'completata',
        positionAtEnd: this.ordineCount,
        savedCount: this.ordiniSaved.size,
        ...(this.modaRientroTerzista ? { movimentiRettificaCount: { ...this.movimentiRettificaCount } } : {})
      };
      this.modalCtrl.dismiss(outcome, 'confirm');
      return;
    }
    this.activeOrdineIndex = nextIndex;
    // Remount sub-componenti (FR-024)
    this.checkListVisible = false;
    setTimeout(() => {
      this.hydrateActiveOrdine();
      this.applyDefaultsFromRisorsa();
      this.checkListVisible = true;
    }, 0);
  };

  private onStatoControlli = (_stato: number) => {
    // In modalità sequenza, statoControlliQualita riflette lo stato dell'ordine attivo;
    // l'aggregato di gruppo viene fissato a 2 solo a fine sequenza (vedi advanceToNextOrdine).
    if (this.ordineCount <= 1) {
      this.statoControlliQualita = _stato;
    }
    if (_stato !== 2) {
      return;
    }
    // Idempotenza FR-023: se l'ordine attivo è già stato salvato, ignora.
    if (this.ordiniSaved.has(this.activeOrdineIndex)) {
      return;
    }
    // Auto-advance ~1s sugli ordini senza CQ configurato (FR-001e, US3)
    if (!this.isCqConfiguredForActiveOrdine()) {
      if (this.autoAdvanceTimer) {
        return;
      }
      this.autoAdvanceTimer = setTimeout(() => {
        this.autoAdvanceTimer = null;
        this.advanceToNextOrdine();
      }, 1000);
      return;
    }
    if (this.modaRientroTerzista) {
      this.saveActiveOrdineRettifica();
    } else {
      this.saveActiveOrdineLegacy();
    }
  };

  // True se l'ordine attivo ha controlli qualità configurati (lista boxes non vuota oppure quantità da controllare)
  // FR-001e: gli ordini senza CQ effettiva (lista vuota, nessun check) ottengono auto-advance silenzioso.
  isCqConfiguredForActiveOrdine = (): boolean => {
    if (!this.modaRientroTerzista) {
      // In modalità legacy non interpretiamo "no-CQ" — preserviamo il comportamento attuale.
      return true;
    }
    return (this.boxes && this.boxes.length > 0);
  };

  private saveActiveOrdineLegacy = () => {
    this.loading = true;
    const dataEvento = Math.floor(new Date().getTime() / 1000);
    const isSequence = this.ordineCount > 1;
    let payload: OrdineGroup;
    let qtaConfermaSend: number;
    let qtaScartataSend: number;
    let qtaNcSend: number;
    if (!isSequence) {
      // Legacy single-ordine (interfaccia-manuale e simili): comportamento 1:1 con il pre-feature.
      this.ordine.contenitori = this.boxes;
      this.qtaConferma += this.ordine.quantitaLavorata ?? 0;
      this.qtaNonConforme += this.ordine.quantitaNonConforme ?? 0;
      payload = this.ordine;
      qtaConfermaSend = this.qtaConferma;
      qtaScartataSend = this.ordine.quantitaScartata;
      qtaNcSend = this.qtaNonConforme;
    } else {
      // Sequence mode: proiezione single-ordine (FR-001g) per evitare duplicazione backend.
      const active = this.activeOrdine;
      const singleProjection: OrdineGroup = { ...(active as OrdineGroup), ordini: [active] };
      singleProjection.contenitori = this.boxes;
      payload = singleProjection;
      qtaConfermaSend = this.qtaConferma + (active.quantitaLavorata ?? 0);
      qtaScartataSend = active.quantitaScartata;
      qtaNcSend = this.qtaNonConforme + (active.quantitaNonConforme ?? 0);
    }
    this.avanzamentoService.invioAvanzaQualitaGroup(this.organizzazioneService.organizzazioneId, dataEvento,
      payload, qtaConfermaSend, qtaScartataSend, qtaNcSend, this.authService.currentUserValue.userName)
      .then(() => {
        this.ordiniSaved.add(this.activeOrdineIndex);
        if (!isSequence) {
          // Backwards-compat: chiusura come pre-feature
          this.modalCtrl.dismiss(true, 'confirm');
          return;
        }
        this.advanceToNextOrdine();
      })
      .catch(() => {
        this.saveMessage.messageError("Ci sono problemi con l'avanzamento qualità.");
        if (isSequence) {
          const outcome: CqSequenceOutcome = {
            outcome: 'errore',
            positionAtEnd: this.activeOrdineIndex + 1,
            savedCount: this.ordiniSaved.size,
            errorMessage: "Ci sono problemi con l'avanzamento qualità."
          };
          this.modalCtrl.dismiss(outcome, 'error');
        }
        // In legacy non-sequence non dismetto: lascio la modale aperta come il pre-feature.
      })
      .finally(() => this.loading = false);
  };

  private saveActiveOrdineRettifica = () => {
    // Rev. 2026-05-19 (clarification Q2 + Q3): riuso codMagTransfer/codMagTransferNc legacy;
    // pre-validazione bloccante FR-019 centralizzata in generateRettificaMovimenti.
    const executedBy = this.organizzazioneService.codiceOperatore ?? this.authService.currentUserValue.userName;
    const result = this.generateRettificaMovimenti({
      activeOrdine: this.activeOrdine,
      boxesOk: this.boxesOk,
      boxesNc: this.boxesNc,
      codMagTransfer: this.codMagTransfer,
      codMagAreaTransfer: this.codMagAreaTransfer,
      codMagTransferNc: this.codMagTransferNc,
      codMagAreaTransferNc: this.codMagAreaTransferNc,
      organizzazioneId: this.organizzazioneService.organizzazioneId,
      executedBy
    });
    if (result.kind === 'block') {
      // FR-019: errore bloccante. Rollback idempotenza (non più necessario marcare l'indice come saved).
      this.ordiniSaved.delete(this.activeOrdineIndex);
      this.saveMessage.messageError(result.errorMessage);
      return;  // NO save, NO advance — l'operatore deve risolvere
    }
    // result.kind === 'ok'
    if (result.movimenti.length === 0) {
      // FR-018: zero box su entrambi i lati AND/OR magazzini vuoti → conferma ammessa, avanza.
      this.ordiniSaved.add(this.activeOrdineIndex);
      this.advanceToNextOrdine();
      return;
    }
    this.loading = true;
    const conformeMag = this.codMagTransfer;
    Promise.all(result.movimenti.map(m => this.movimentiService.createMovimentoAsync(m)))
      .then(() => {
        // Aggiorna contatori (FR-020)
        result.movimenti.forEach(m => {
          if (m.codMagazzinoIn === conformeMag) {
            this.movimentiRettificaCount.conforme += 1;
          } else {
            this.movimentiRettificaCount.nc += 1;
          }
        });
        this.ordiniSaved.add(this.activeOrdineIndex);
        this.advanceToNextOrdine();
      })
      .catch(() => {
        const errMsg = "Ci sono problemi con la creazione dei movimenti di rettifica.";
        this.saveMessage.messageError(errMsg);
        const outcome: CqSequenceOutcome = {
          outcome: 'errore',
          positionAtEnd: this.activeOrdineIndex + 1,
          savedCount: this.ordiniSaved.size,
          movimentiRettificaCount: { ...this.movimentiRettificaCount },
          errorMessage: errMsg
        };
        this.modalCtrl.dismiss(outcome, 'error');
      })
      .finally(() => this.loading = false);
  };

  generateRettificaMovimenti = (p: {
    activeOrdine: Ordine;
    boxesOk: Contenitore[];
    boxesNc: Contenitore[];
    codMagTransfer: string;
    codMagAreaTransfer: string;
    codMagTransferNc: string;
    codMagAreaTransferNc: string;
    organizzazioneId: number;
    executedBy: string;
  }): { kind: 'ok'; movimenti: Movimento[] } | { kind: 'block'; errorMessage: string } => {
    // Pre-validazione FR-019 (clarification 2026-05-19 Q3): box presenti senza magazzino destinazione = blocco.
    if (p.boxesOk.length > 0 && !p.codMagTransfer) {
      return {
        kind: 'block',
        errorMessage: `Magazzino Conforme non valorizzato per ${p.boxesOk.length} contenitori OK: selezionare un magazzino o spostare i box su NC tramite lo switch OK/NC.`
      };
    }
    if (p.boxesNc.length > 0 && !p.codMagTransferNc) {
      return {
        kind: 'block',
        errorMessage: `Magazzino Non Conforme non valorizzato per ${p.boxesNc.length} contenitori NC: selezionare un magazzino o spostare i box su OK tramite lo switch OK/NC.`
      };
    }
    if (!p.activeOrdine?.codMagPrincipale) {
      return {
        kind: 'block',
        errorMessage: "Magazzino principale dell'ordine non valorizzato: impossibile generare movimenti di rettifica."
      };
    }
    const movimenti: Movimento[] = [];
    if (p.codMagTransfer && p.boxesOk.length > 0) {
      for (const box of p.boxesOk) {
        movimenti.push(this.buildMovimentoRettifica(p.activeOrdine, box, p.codMagTransfer, p.codMagAreaTransfer, p.organizzazioneId, p.executedBy));
      }
    }
    if (p.codMagTransferNc && p.boxesNc.length > 0) {
      for (const box of p.boxesNc) {
        movimenti.push(this.buildMovimentoRettifica(p.activeOrdine, box, p.codMagTransferNc, p.codMagAreaTransferNc, p.organizzazioneId, p.executedBy));
      }
    }
    return { kind: 'ok', movimenti };
  };

  private buildMovimentoRettifica = (activeOrdine: Ordine, box: Contenitore,
                                     codMagIn: string, codMagAreaIn: string,
                                     organizzazioneId: number, executedBy: string): Movimento => {
    return {
      organizzazioneId,
      id: 0,
      dataUnixRegistrazione: Math.floor(new Date().getTime() / 1000),
      rifOdP: activeOrdine.rifOdP,
      numFase: activeOrdine.numFase,
      codLav: activeOrdine.lavorazione?.codice,
      idRigaMateriale: activeOrdine.idRiga ?? 0,
      codArt: box.codArt,
      varianteArt: box.varianteArt,
      unitaMisura: box.udM,
      magUdM: box.udM,
      quantita: box.giacenza,
      quantitaUdM2: box.giacenzaUdM2,
      coeffX1: 1,
      codLottoAlfa: box.codLottoAlfa,
      codBox: box.codice,
      codMagazzinoOut: activeOrdine.codMagPrincipale,
      codMagAreaOut: activeOrdine.codAreaMagPrinc,
      codMagazzinoIn: codMagIn,
      codMagAreaIn: codMagAreaIn,
      contesto: 3,
      tipoMovimento: 1,
      tipoQuantita: box.tipoBox,
      udM2: box.udM2,
      idAnagrafica: activeOrdine.codCliente,
      saldaRiga: false,
      tipoDoc: 2,
      executedBy
    } as Movimento;
  };

  setBoxNc = (box: Contenitore) => {
    box.isNc = !box.isNc;
    this.updateQta();
  };

  selectBox = (box: Contenitore) => {
    if (this.boxes && this.boxes.length > 0) {
      const valid = this.boxes
        .filter((_con: Contenitore) => _con.codice === box.codice);
      if (valid && valid.length) {
        return false;
      }
    }
    if (box && box.giacenza > 0 &&
      this.boxes.indexOf(box) === -1) {
      box.qtaCarico = box.giacenza;
      if (this.setMateriale(box)) {
        this.boxes.push(box);
        this.updateQta();
      } else {
        this.saveMessage.messageError("Articolo nel contenitore non valido.");
      }
    }
  };

  setMateriale = (box: Contenitore) => {
    // La materiale arricchita da `getMateriale()` è popolata sulla TESTATA del gruppo
    // (`this.ordine.materiale`), non sui singoli `ordini[]`. Comportamento 1:1 con pre-feature.
    if (this.ordine.materiale == null || this.ordine.materiale.length === 0){
      return;
    }
    let materialeValid = false;
    this.ordine.materiale.forEach((_materia: Materiale) => {
      if (_materia.codArt !== box.codArt) {
        return;
      }
      const movimento: Movimento = {
        organizzazioneId: this.organizzazioneService.organizzazioneId,
        id: 0,
        dataUnixRegistrazione: Math.floor(new Date().getTime() / 1000),
        rifOdP: _materia.rifOdP,
        numFase: _materia.numFase,
        codLav: this.ordine?.lavorazione?.codice,
        idRigaMateriale: _materia.idRigaMateriale,
        codArt: _materia.codArt,
        varianteArt: _materia.variante,
        unitaMisura: _materia.unitaMisura,
        magUdM: _materia.unitaMisura,
        quantita: 0,
        consumo: box.qtaCarico,
        coeffX1: _materia.quantitaX1 > 0 ? _materia.quantitaX1 : 1,
        codLottoAlfa: box.codLottoAlfa,
        codMagazzinoOut: box.codMagazzino,
        codMagAreaOut: box.codMagArea,
        codBox: box.codice,
        contesto: 0,
        tipoMovimento: -1,
        currentGiacenza: this.unitConverter.convert(box.giacenza, box.udM, _materia.unitaMisura),
        currentGiacenzaUdM2: box.giacenzaUdM2,
        udM2: box.udM2,
        executedBy: this.organizzazioneService.codiceOperatore ?? this.authService.currentUserValue.userName
      };
      if (!_materia.movimenti) {
        _materia.movimenti = [];
      }
      _materia.movimenti.push(movimento);
      materialeValid = true;
    });
    return materialeValid || !this.checkMaterialeDistinta;
  };

  removeBox = (box: Contenitore) => {
    this.boxes = this.boxes.filter((_con: Contenitore) => _con !== box);
    this.updateQta();
  };

  changeQta = () => {
    const countOk = this.boxes
      .reduce((count, p) => !p.isNc ? count + 1 : count, 0);
    this.boxes.forEach((box: Contenitore) => {
      const coeff = box.giacenzaUdM2 > 0 ? box.giacenzaUdM2 / box.giacenza : 1;
      box.giacenza = box.isNc && (this.boxes.length - countOk) > 0 ?
        this.qtaNonConforme / (this.boxes.length - countOk) :
        this.qtaConferma / countOk;
      box.qtaCarico = box.giacenza;
      box.giacenzaUdM2 = box.giacenza * coeff;
    });
  }

  updateQta = () => {
    this.qtaConferma = 0;
    this.qtaNonConforme = 0;
    this.boxesOk = [];
    this.boxesNc = [];
    this.boxes.forEach((box: Contenitore) => {
      if (box.isNc) {
        this.qtaNonConforme += box.giacenza;
        this.boxesNc.push(box);
      }
      else {
        this.qtaConferma += box.giacenza;
        this.boxesOk.push(box);
      }
    })
  };

  cancel = () => {
    if (this.ordineCount > 1) {
      const outcome: CqSequenceOutcome = {
        outcome: 'interrotta',
        positionAtEnd: this.activeOrdineIndex + 1,
        savedCount: this.ordiniSaved.size,
        ...(this.modaRientroTerzista ? { movimentiRettificaCount: { ...this.movimentiRettificaCount } } : {})
      };
      this.modalCtrl.dismiss(outcome, 'cancel');
      return;
    }
    // Compat legacy: chiusura come pre-feature (consumers ascoltano solo il role)
    this.modalCtrl.dismiss(false, 'cancel');
  };
}
